/**
 * Read-only previews of iManage documents without a renderer: the visible text
 * of a Word document with its headings, and the headers and plain-text body of
 * a filed email. Both are extraction only — no markup is ever rendered as HTML.
 *
 * @module components/apps/imanage/imanagePreview
 */
import { readZipEntries, readZipEntryText } from "@amoreg/shared/zip";
import { htmlText } from "@amoreg/shared/htmlText";

export interface PreviewParagraph {
  readonly text: string;
  readonly role: "title" | "heading" | "body";
}

const ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

export function decodeXmlText(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, name: string) => {
    if (name[0] === "#") {
      const code = name[1] === "x" || name[1] === "X" ? parseInt(name.slice(2), 16) : Number(name.slice(1));
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[name.toLowerCase()] ?? match;
  });
}

const BOLD_ON = /<w:b(?:\s+w:val="(?:true|1|on)")?\s*\/>/;

/**
 * The paragraphs of `word/document.xml`, as a reader sees them: deleted and
 * moved-away revisions are left out; tabs and breaks are kept. A paragraph is
 * a title or heading when its style says so (Title, Heading 1–9), or when all
 * its text is bold (large bold text is a title).
 */
// Opening tags end in `(?<!\/)>`: a self-closing `<w:p w:rsidR="…"/>` or a
// paragraph-mark `<w:del w:id="…"/>` must not open an element that then runs
// on to the next closing tag and swallows the text between.
export function docxParagraphs(xml: string, limit = 5000): PreviewParagraph[] {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) throw new Error("This document's XML is not supported.");
  const out: PreviewParagraph[] = [];
  const paragraphs = xml.match(/<w:p(?:\s[^>]*)?(?<!\/)>[\s\S]*?<\/w:p>|<w:p(?:\s[^>]*)?\/>/g) ?? [];
  for (const raw of paragraphs) {
    if (out.length >= limit) break;
    const visible = raw
      .replace(/<w:del(?:\s[^>]*)?(?<!\/)>[\s\S]*?<\/w:del>/g, "")
      .replace(/<w:moveFrom(?:\s[^>]*)?(?<!\/)>[\s\S]*?<\/w:moveFrom>/g, "");
    let text = "";
    const runs = visible.match(/<w:r(?:\s[^>]*)?(?<!\/)>[\s\S]*?<\/w:r>/g) ?? [];
    let textRuns = 0;
    let boldRuns = 0;
    let largest = 0;
    for (const run of runs) {
      const pieces = run.match(/<w:t(?:\s[^>]*)?(?<!\/)>[\s\S]*?<\/w:t>|<w:tab\s*\/>|<w:br(?:\s[^>]*)?\/>|<w:cr\s*\/>/g) ?? [];
      let runText = "";
      for (const piece of pieces) {
        if (piece.startsWith("<w:tab")) runText += "\t";
        else if (piece.startsWith("<w:br") || piece.startsWith("<w:cr")) runText += "\n";
        else runText += decodeXmlText(piece.replace(/^<w:t(?:\s[^>]*)?>|<\/w:t>$/g, ""));
      }
      if (runText.trim()) {
        textRuns++;
        const properties = /<w:rPr>[\s\S]*?<\/w:rPr>/.exec(run)?.[0] ?? "";
        if (BOLD_ON.test(properties)) boldRuns++;
        const size = /<w:sz\s+w:val="(\d+)"/.exec(properties);
        if (size) largest = Math.max(largest, Number(size[1]));
      }
      text += runText;
    }
    const style = /<w:pStyle\s+w:val="([^"]+)"/.exec(visible)?.[1]?.toLowerCase() ?? "";
    const role: PreviewParagraph["role"] =
      style === "title" || (textRuns > 0 && boldRuns === textRuns && largest >= 30)
        ? "title"
        : /^heading\d?$/.test(style) || (textRuns > 0 && boldRuns === textRuns)
          ? "heading"
          : "body";
    out.push({ text, role });
  }
  // Collapse runs of empty paragraphs to one, and trim them from the ends.
  return out.filter((p, i) => p.text.trim() || (i > 0 && out[i - 1]!.text.trim() && i < out.length - 1));
}

export async function docxPreview(bytes: Uint8Array): Promise<PreviewParagraph[]> {
  const entry = readZipEntries(bytes).get("word/document.xml");
  if (!entry) throw new Error("This file is not a Word document.");
  return docxParagraphs(await readZipEntryText(bytes, entry, { maxBytes: 4 * 1024 * 1024 }));
}

export interface EmailPreview {
  readonly from?: string;
  readonly to?: string;
  readonly cc?: string;
  readonly subject?: string;
  readonly date?: string;
  readonly body: string;
}

function splitHeaders(raw: string): { headers: Map<string, string>; body: string } {
  const normalized = raw.replace(/\r\n/g, "\n");
  const end = normalized.indexOf("\n\n");
  const head = end === -1 ? normalized : normalized.slice(0, end);
  const body = end === -1 ? "" : normalized.slice(end + 2);
  const headers = new Map<string, string>();
  let last: string | null = null;
  for (const line of head.split("\n")) {
    if (/^[ \t]/.test(line) && last) {
      headers.set(last, `${headers.get(last)} ${line.trim()}`);
      continue;
    }
    const colon = line.indexOf(":");
    if (colon <= 0) continue;
    last = line.slice(0, colon).trim().toLowerCase();
    if (!headers.has(last)) headers.set(last, line.slice(colon + 1).trim());
  }
  return { headers, body };
}

function decodeTransfer(body: string, encoding: string | undefined): string {
  const kind = encoding?.toLowerCase();
  if (kind === "quoted-printable")
    return decodeUtf8Bytes(
      body.replace(/=\n/g, "").replace(/=([0-9A-F]{2})/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16))),
    );
  if (kind === "base64") {
    try {
      return decodeUtf8Bytes(atob(body.replace(/\s+/g, "")));
    } catch {
      return body;
    }
  }
  return body;
}

const decodeUtf8Bytes = (binary: string) => {
  try {
    return new TextDecoder("utf-8", { fatal: false }).decode(
      Uint8Array.from(binary, (c) => c.charCodeAt(0) & 0xff),
    );
  } catch {
    return binary;
  }
};

/** Headers and the first plain-text part of an RFC 822 message. */
export function parseEmail(raw: string, depth = 0): EmailPreview {
  const { headers, body } = splitHeaders(raw);
  const type = headers.get("content-type") ?? "text/plain";
  let text: string;
  const boundary = /boundary="?([^";]+)"?/i.exec(type)?.[1];
  if (/^multipart\//i.test(type) && boundary && depth < 3) {
    const parts = body.split(`--${boundary}`).slice(1, -1);
    const candidates = parts
      .map((part) => part.replace(/^\n/, ""))
      .map((part) => ({ part, type: splitHeaders(part).headers.get("content-type") ?? "text/plain" }));
    const bodyOf = (part: string) => parseEmail(part, depth + 1).body;
    // The plain text wins; a message with none (HTML plus attachments or
    // inline images) falls back to its HTML part, read as text.
    text = "";
    for (const { part, type: partType } of candidates) {
      if (!/^(text\/plain|multipart\/)/i.test(partType)) continue;
      text = bodyOf(part);
      if (text) break;
    }
    if (!text) {
      const html = candidates.find(({ type: partType }) => /^text\/html/i.test(partType));
      if (html) text = bodyOf(html.part);
    }
  } else if (/^text\/html/i.test(type)) {
    text = decodeXmlText(htmlText(decodeTransfer(body, headers.get("content-transfer-encoding"))));
  } else text = decodeTransfer(body, headers.get("content-transfer-encoding"));
  const pick = (name: string) => headers.get(name) || undefined;
  const preview: { -readonly [K in keyof EmailPreview]?: EmailPreview[K] } = {
    body: text.replace(/\n{3,}/g, "\n\n").trim(),
  };
  for (const name of ["from", "to", "cc", "subject", "date"] as const) {
    const value = pick(name);
    if (value) preview[name] = value;
  }
  return preview as EmailPreview;
}
